import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import backgroundImage from "./HeaderSlider_04.webp";
import { useUser } from "./../Home/UserContext";
import { loginUser } from "./../../services/userServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const BRAND = {
  // Si tu veux coller au MonAppliAG, tu peux garder ces valeurs,
  // sinon remplace BRAND.blue par ta couleur primary si besoin.
  blue: "#013E8E",
  green: "#4CA82F",
  blueLight: "#038DE6",
};

function LoginPage() {
  useScrollToTop();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { updateUser } = useUser();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const cleanEmail = email.trim().toLowerCase();
      const data = await loginUser(cleanEmail, password);

      if (!data?.userInfo || !data?.token) {
        setErrorMessage("Email ou mot de passe incorrect. Veuillez réessayer.");
        return;
      }

      // ✅ fallback: si l’API renvoie mail ou rien, on garde l’email saisi
      const safeUserInfo = {
        ...data.userInfo,
        email: data.userInfo?.email || data.userInfo?.mail || cleanEmail,
      };

      localStorage.setItem("token", data.token);
      localStorage.setItem("userInfo", JSON.stringify(safeUserInfo));
      updateUser(safeUserInfo);

      navigate("/");
    } catch (error) {
      setErrorMessage(
        "Impossible de se connecter pour le moment. Veuillez réessayer plus tard.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* overlay noir */}
      <div className="absolute inset-0 bg-black/55" />

      {/* glow coloré */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at top, rgba(1,62,142,0.55), transparent 55%), radial-gradient(circle at bottom, rgba(76,168,47,0.55), transparent 55%)",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="bg-white/50 backdrop-blur-md border border-white/50 shadow-2xl rounded-2xl p-8 md:p-10">
          {/* ✅ Logo + titre */}
          <div className="text-center mb-6">
            <img
              src="/img/logos/logo_monappliclub.png"
              alt="MonAppliClub"
              className="h-14 md:h-16 mx-auto mb-3 select-none"
              draggable="false"
              onError={(e) => {
                // fallback si tu n'as pas ce logo
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
              Connexion
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              Accédez à votre espace MonAppliClub
            </p>
          </div>

          {/* ✅ Erreur */}
          {errorMessage && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Email
              </label>
              <input
                type="email"
                placeholder="Votre email"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none
                           focus:ring-2 focus:ring-[#013E8E]/25 focus:border-[#013E8E] transition"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            {/* Password + toggle (même UX que MonAppliAG) */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Mot de passe
              </label>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Votre mot de passe"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-28 outline-none
                             focus:ring-2 focus:ring-[#013E8E]/25 focus:border-[#013E8E] transition"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2
                             text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showPassword ? "Masquer" : "Afficher"}
                </button>
              </div>
            </div>

            {/* CTA gradient */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`
                w-full rounded-xl px-4 py-3 font-semibold shadow-sm transition border
                ${isSubmitting ? "cursor-not-allowed opacity-80" : "hover:opacity-95"}
              `}
              style={{
                borderColor: BRAND.blue,
                color: "white",
                backgroundImage: `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.green})`,
              }}
            >
              {isSubmitting ? "Connexion..." : "Se connecter"}
            </button>

            <div className="flex items-center justify-between pt-2 text-sm">
              <Link
                to="/forgot-password"
                className="hover:underline"
                style={{ color: BRAND.blue }}
              >
                Mot de passe oublié ?
              </Link>

              <Link to="/register" className="text-slate-700 hover:underline">
                Créer un compte
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-white/85 mt-4">
          MonAppliClub • Gestion de club simplifiée
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
